// Модал ручного создания/редактирования блока: шаг «выбор типа» (для create
// без заданного kind) + форма по типу. Разметку модала делаем сами — общий
// Modal.tsx интегратор сведёт позже (см. CONTRACT.md).
import { useEffect, useId, useRef, useState } from 'react';
import type {
  BaggageSegment,
  FlightSegment,
  LodgingSegment,
  Segment,
  SegmentKind,
  SimpleCostSegment,
  TransferSegment,
} from '../../types';
import { useStore } from '../../store';
import { KindPicker } from './KindPicker';
import { FlightForm } from './FlightForm';
import { TransferForm } from './TransferForm';
import { LodgingForm } from './LodgingForm';
import { SimpleCostForm } from './SimpleCostForm';
import { BaggageForm } from './BaggageForm';
import styles from './SegmentFormModal.module.css';
import { useT } from '../../i18n';

export interface SegmentFormModalProps {
  open: boolean;
  mode: 'create' | 'edit';
  kind?: SegmentKind;
  segmentId?: string;
  onClose(): void;
  /**
   * Шаг выбора типа зовёт это вместо выбора конкретного kind — владелец
   * (LibraryPanel) закрывает этот модал и открывает ParseModal сам: модал не
   * должен открывать модал, иначе состояние размажется по двум местам.
   */
  onPickSource?(): void;
}

function renderForm(mode: 'create' | 'edit', kind: SegmentKind, segment: Segment | undefined, onClose: () => void) {
  switch (kind) {
    case 'flight':
      return <FlightForm mode={mode} segment={segment as FlightSegment | undefined} onClose={onClose} />;
    case 'transfer':
      return <TransferForm mode={mode} segment={segment as TransferSegment | undefined} onClose={onClose} />;
    case 'lodging':
      return <LodgingForm mode={mode} segment={segment as LodgingSegment | undefined} onClose={onClose} />;
    case 'taxi':
    case 'food':
    case 'misc':
      return (
        <SimpleCostForm
          mode={mode}
          kind={kind}
          segment={segment as SimpleCostSegment | undefined}
          onClose={onClose}
        />
      );
    case 'baggage':
      return <BaggageForm mode={mode} segment={segment as BaggageSegment | undefined} onClose={onClose} />;
    default:
      return null;
  }
}

export function SegmentFormModal({ open, mode, kind, segmentId, onClose, onPickSource }: SegmentFormModalProps) {
  const state = useStore();
  const t = useT();
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [pickedKind, setPickedKind] = useState<SegmentKind | undefined>(kind);

  useEffect(() => {
    if (open) setPickedKind(kind);
  }, [open, kind, segmentId]);

  const segment = mode === 'edit' && segmentId ? state.library[segmentId] : undefined;
  const activeKind: SegmentKind | undefined = mode === 'edit' ? segment?.kind : kind ?? pickedKind;
  const isPickStep = mode === 'create' && !activeKind;

  useEffect(() => {
    if (open && isPickStep) {
      const firstBtn = bodyRef.current?.querySelector<HTMLButtonElement>('button');
      firstBtn?.focus();
    }
  }, [open, isPickStep]);

  if (!open) return null;

  const notFound = mode === 'edit' && !segment;
  const wide = activeKind === 'flight';
  const title = notFound
    ? t('form.notFound')
    : isPickStep
      ? t('form.new')
      : `${t(mode === 'edit' ? 'form.edit' : 'form.new')}${activeKind ? ': ' + t(`kind.${activeKind}`) : ''}`;

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      return;
    }
    if (e.key === 'Tab') {
      const focusables = panelRef.current?.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (!focusables || focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  return (
    <div className={styles.overlay} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        ref={panelRef}
        className={`${styles.panel} ${wide ? styles.wide : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={handleKeyDown}
      >
        <div className={styles.header}>
          <h2 id={titleId} className={styles.title}>
            {title}
          </h2>
          <button type="button" className={styles.closeBtn} aria-label={t('form.close')} onClick={onClose}>
            ×
          </button>
        </div>
        <div className={styles.body} ref={bodyRef}>
          {notFound ? (
            <p className={styles.fallback}>{t('form.gone')}</p>
          ) : isPickStep ? (
            <KindPicker onPick={setPickedKind} onPickSource={onPickSource} />
          ) : (
            activeKind && renderForm(mode, activeKind, segment, onClose)
          )}
        </div>
      </div>
    </div>
  );
}
